import { type FunctionComponent, type PropsWithChildren, useState, useEffect, createContext } from 'react';
import { useRouteChange } from '../../hooks/routing/route-change.hook';

type ScrollLockContextState = {
	isScrollLocked: boolean;
	setIsScrollLocked: (lockScrolling: boolean) => void;
};

const initialState = {
	isScrollLocked: false,
	setIsScrollLocked: () => {}
};

export const ScrollLockContext = createContext<ScrollLockContextState>(initialState);

export const ScrollLockContextProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
	const [isScrollLocked, setIsScrollLocked] = useState(false);

	useRouteChange(() => {
		setIsScrollLocked(false);
	});

	/**
	 * Lock scrolling with overflow-hidden so the document keeps its height and the scroll position is preserved.
	 * Pad the body by the scrollbar width so content doesn't shift when the scrollbar disappears.
	 */
	useEffect(() => {
		if (!isScrollLocked) {
			return;
		}
		const { body, documentElement } = document;
		const scrollbarWidth = window.innerWidth - documentElement.clientWidth;
		const previousOverflow = body.style.overflow;
		const previousPaddingRight = body.style.paddingRight;
		body.style.overflow = 'hidden';
		if (scrollbarWidth > 0) {
			body.style.paddingRight = `${scrollbarWidth}px`;
		}
		return () => {
			body.style.overflow = previousOverflow;
			body.style.paddingRight = previousPaddingRight;
		};
	}, [isScrollLocked]);

	const value = {
		isScrollLocked,
		setIsScrollLocked
	};
	return <ScrollLockContext.Provider value={value}>{children}</ScrollLockContext.Provider>;
};
